import { useCallback, useMemo } from 'react';
import { formatCategoryName } from '../../../../ui/formatCategoryName';

/**
 * Karten-Aktionen der mobilen Ansicht mit Toast und Rückgängig-Eintrag — Einzel-Aktionen
 * (Viewer-Aktionszeile) und Batch-Aktionen (Auswahl-Modus). Die Mutationen selbst kommen
 * unverändert aus `useDeckCardActions`; hier liegt nur die Undo-Registrierung an der
 * Vorwärts-Callsite plus die Rückmeldung an den Nutzer.
 *
 * Registriert wird immer NACH der erfolgreichen Mutation, der Karten-Stand dafür aber VOR
 * ihr festgehalten: `removeCard` liefert nur `boolean` und das optimistische Update hat die
 * Zeile dann schon aus dem Deck genommen. Ein Eintrag vor der Mutation würde bei einem
 * Fehlschlag stehenbleiben und beim Rückgängig eine Karte wieder-hinzufügen, die nie
 * verschwunden ist.
 *
 * `getCardLabels` braucht nur der Batch-Zweig: `registerBatch` bekommt fertige
 * Unter-Einträge, während `registerRemove` seine Labels selbst nachschlägt.
 */
export function useMobileDeckActions({ cardActions, undo, pushToast, deck, getCardLabels }) {
  const findCard = useCallback((scryfallId) => (
    (deck?.cards || []).find((c) => (c.scryfallId || c.id) === scryfallId) || null
  ), [deck?.cards]);

  const undoAction = useCallback((entryId) => (
    entryId != null ? { label: 'Rückgängig', onClick: () => undo.undoEntry(entryId) } : undefined
  ), [undo]);

  const quantityDelta = useCallback(async (card, delta) => {
    const scryfallId = card.scryfallId || card.id;
    const result = await cardActions.handleQuantityDelta(scryfallId, delta);
    if (!result?.ok || !result.card) return result;
    if (result.card.isCommander) return result; // Commander-Aktionen sind keine Undo-Paare
    if (result.removed) {
      const entryId = undo.registerRemove(result.card, result.previousQuantity);
      pushToast({
        tone: 'success',
        message: `${result.card.name} entfernt`,
        action: undoAction(entryId),
      });
    } else {
      undo.registerQuantity(scryfallId, delta);
    }
    return result;
  }, [cardActions, undo, pushToast, undoAction]);

  const removeSingle = useCallback(async (card) => {
    const scryfallId = card.scryfallId || card.id;
    const snapshot = findCard(scryfallId) || card;
    const ok = await cardActions.removeCard(scryfallId);
    if (!ok) return false;
    const entryId = snapshot.isCommander
      ? null
      : undo.registerRemove(snapshot, Number(snapshot.quantity) || 1);
    pushToast({
      tone: 'success',
      message: `${snapshot.name || card.name} entfernt`,
      action: undoAction(entryId),
    });
    return true;
  }, [cardActions, findCard, undo, pushToast, undoAction]);

  const moveSingle = useCallback(async (card, categoryId, categoryName) => {
    const scryfallId = card.scryfallId || card.id;
    const from = { id: card.categoryId ?? null, name: card.category, isCommander: false };
    const result = await cardActions.moveCardToCategory(scryfallId, categoryId);
    if (!result?.ok) return result;
    const entryId = undo.registerMove({ cardId: scryfallId, from });
    const target = formatCategoryName(result.category?.name || categoryName);
    pushToast({
      tone: 'success',
      message: `${card.name} nach ${target} verschoben`,
      action: undoAction(entryId),
    });
    return result;
  }, [cardActions, undo, pushToast, undoAction]);

  const moveBatch = useCallback(async (cards, categoryId, categoryName) => {
    const before = cards.map((c) => ({
      scryfallId: c.scryfallId || c.id,
      fromCategoryId: c.categoryId ?? null,
    }));
    const result = await cardActions.moveCardsBatch(
      before.map((m) => ({ scryfallId: m.scryfallId, categoryId }))
    );
    // null = kompletter Request-Fehlschlag, den useDeckCardActions schon gemeldet hat.
    if (!result) return null;
    // `ok` ist konstant true — nur succeeded/failed entscheiden.
    const succeededIds = new Set((result.succeeded || []).map((s) => s.scryfallId));
    const done = before.filter((m) => succeededIds.has(m.scryfallId));
    const failedIds = (result.failed || []).map((f) => f.scryfallId);
    const target = result.succeeded?.[0]?.category?.name || categoryName;
    if (done.length === 0) {
      pushToast({
        tone: 'error',
        message: result.failed?.[0]?.error || 'Karten konnten nicht verschoben werden.',
      });
      return { ...result, failedIds };
    }
    const entryId = undo.registerBatchMove(done);
    if ((result.failed || []).length === 0) {
      pushToast({
        tone: 'success',
        message: `${formatCardCount(done.length)} nach ${formatCategoryName(target)} verschoben`,
        action: undoAction(entryId),
      });
    } else {
      pushToast({
        tone: 'error',
        message: `${done.length} von ${before.length} verschoben — ${result.failed[0].error}`,
        action: undoAction(entryId),
      });
    }
    return { ...result, failedIds };
  }, [cardActions, undo, pushToast, undoAction]);

  // Commander ist serverseitig eine Kategorie: die Gegenaktion ist ein normales
  // Zurückverschieben. Nach dem ersten Fehlschlag (typisch: Commander-Obergrenze erreicht)
  // bricht die Schleife ab — die übrigen scheiterten am selben Grund, und `setAsCommander`
  // meldet jeden Fehlschlag selbst als Toast.
  const moveToCommander = useCallback(async (cards, onCategoryChange) => {
    const pending = cards.filter((c) => !c.isCommander);
    const done = [];
    const failedIds = [];
    for (const card of pending) {
      const scryfallId = card.scryfallId || card.id;
      if (failedIds.length > 0) {
        failedIds.push(scryfallId);
        continue;
      }
      const ok = await cardActions.setAsCommander(scryfallId, true, onCategoryChange);
      if (ok) done.push({ scryfallId, fromCategoryId: card.categoryId ?? null });
      else failedIds.push(scryfallId);
    }
    if (done.length > 0) {
      const entryId = undo.registerBatchMove(done);
      pushToast({
        tone: 'success',
        message: done.length === 1 && pending.length === 1
          ? `${pending[0].name} als Commander gesetzt`
          : `${formatCardCount(done.length)} als Commander gesetzt`,
        action: undoAction(entryId),
      });
    }
    return { succeeded: done.length, total: pending.length, failedIds };
  }, [cardActions, undo, pushToast, undoAction]);

  const quantityBatch = useCallback(async (cards, delta) => {
    const subEntries = [];
    const failedIds = [];
    let succeeded = 0;
    let firstError = null;
    for (const card of cards) {
      const scryfallId = card.scryfallId || card.id;
      const result = await cardActions.handleQuantityDelta(scryfallId, delta);
      if (!result?.ok) {
        firstError = firstError || 'Kartenmenge konnte nicht geändert werden.';
        failedIds.push(scryfallId);
        continue;
      }
      succeeded += 1;
      const snapshot = result.card;
      if (!snapshot || snapshot.isCommander) continue;
      if (result.removed) {
        subEntries.push({
          type: 'remove',
          scryfallId,
          quantity: result.previousQuantity,
          categoryId: snapshot.categoryId ?? null,
          labels: snapshot.deckCardEntryId ? getCardLabels?.(snapshot.deckCardEntryId) || [] : [],
        });
      } else {
        subEntries.push({ type: 'quantity', scryfallId, delta });
      }
    }
    if (succeeded === 0) {
      pushToast({ tone: 'error', message: firstError || 'Kartenmenge konnte nicht geändert werden.' });
      return { succeeded, total: cards.length, failedIds };
    }
    const entryId = subEntries.length > 0 ? undo.registerBatch(subEntries) : null;
    pushToast({
      tone: firstError ? 'error' : 'success',
      message: firstError
        ? `${succeeded} von ${cards.length} angepasst — ${firstError}`
        : `Menge für ${formatCardCount(succeeded)} angepasst`,
      action: undoAction(entryId),
    });
    return { succeeded, total: cards.length, failedIds };
  }, [cardActions, undo, pushToast, undoAction, getCardLabels]);

  const removeBatch = useCallback(async (cards) => {
    // Alle Stände vor der ersten Mutation festhalten: `deck` in dieser Closure friert
    // während der Schleife ohnehin ein, und nach dem Entfernen ist die Zeile weg.
    const snapshots = cards.map((card) => {
      const scryfallId = card.scryfallId || card.id;
      const snapshot = findCard(scryfallId) || card;
      return {
        scryfallId,
        isCommander: Boolean(snapshot.isCommander),
        entry: {
          type: 'remove',
          scryfallId,
          quantity: Number(snapshot.quantity) || 1,
          categoryId: snapshot.categoryId ?? null,
          labels: snapshot.deckCardEntryId ? getCardLabels?.(snapshot.deckCardEntryId) || [] : [],
        },
      };
    });

    const subEntries = [];
    const failedIds = [];
    let succeeded = 0;
    let firstError = null;
    for (const snapshot of snapshots) {
      const ok = await cardActions.removeCard(snapshot.scryfallId);
      if (!ok) {
        firstError = firstError || 'Karte konnte nicht entfernt werden.';
        failedIds.push(snapshot.scryfallId);
        continue;
      }
      succeeded += 1;
      if (!snapshot.isCommander) subEntries.push(snapshot.entry);
    }
    if (succeeded === 0) {
      pushToast({ tone: 'error', message: firstError || 'Karten konnten nicht entfernt werden.' });
      return { succeeded, total: cards.length, failedIds };
    }
    const entryId = subEntries.length > 0 ? undo.registerBatch(subEntries) : null;
    pushToast({
      tone: firstError ? 'error' : 'success',
      message: firstError
        ? `${succeeded} von ${cards.length} entfernt — ${firstError}`
        : `${formatCardCount(succeeded)} entfernt`,
      action: undoAction(entryId),
    });
    return { succeeded, total: cards.length, failedIds };
  }, [cardActions, findCard, undo, pushToast, undoAction, getCardLabels]);

  return useMemo(
    () => ({ quantityDelta, removeSingle, moveSingle, moveBatch, moveToCommander, quantityBatch, removeBatch }),
    [quantityDelta, removeSingle, moveSingle, moveBatch, moveToCommander, quantityBatch, removeBatch]
  );
}

/** „1 Karte" / „5 Karten" — Toast- und Dialog-Texte zählen auch die Einer-Auswahl. */
export function formatCardCount(count) {
  return count === 1 ? '1 Karte' : `${count} Karten`;
}

export default useMobileDeckActions;
